<script setup lang="ts">
import SourceButton from '~/components/ui/SourceButton.vue'
import ConfirmOverlay from '~/components/app/ConfirmOverlay.vue'
import { useAppLayoutFooterFixedBottomLiftPx } from '~/composables/ui/useAppLayoutFooterFixedBottomLiftPx'
import { useSeasonalCornerBottomInsetPx } from '~/composables/ui/useSeasonalCornerBottomInsetPx'
import { APP_CORNER_BOTTOM_MARGIN_PX } from '~/composables/ui/useSourceToast'
import {
  DISCARD_UNSAVED_CANCEL_TEXT,
  DISCARD_UNSAVED_CONFIRM_TEXT,
  DISCARD_UNSAVED_DESCRIPTION,
  DISCARD_UNSAVED_TITLE,
} from '~/composables/ui/useDiscardUnsavedGuard'

defineProps<{
  visible: boolean
  saving: boolean
  announcement: string
  showDiscardConfirm: boolean
}>()

const emit = defineEmits<{
  cancel: []
  save: []
  confirmDiscard: []
  cancelDiscard: []
}>()

const seasonalCornerBottomInsetPx = useSeasonalCornerBottomInsetPx()
const { extraBottomPx: footerLiftPx } = useAppLayoutFooterFixedBottomLiftPx()

function overlayBottom(): string {
  const bottomPx =
    APP_CORNER_BOTTOM_MARGIN_PX +
    seasonalCornerBottomInsetPx.value +
    footerLiftPx.value
  return `${bottomPx}px`
}

const overlayStyle = computed(() => ({ bottom: overlayBottom() }))
</script>

<template>
  <Teleport to="body">
    <Transition name="org-reorder-overlay">
      <div
        v-if="visible"
        class="org-reorder-overlay"
        :style="overlayStyle"
        role="status"
      >
        <span class="material-symbols-rounded org-reorder-overlay__icon" aria-hidden="true">info</span>
        <p class="org-reorder-overlay__label text-xs">Ordre des domaines modifié</p>
        <p class="org-reorder-live" aria-live="polite">{{ announcement }}</p>
        <div class="org-reorder-overlay__actions">
          <SourceButton type="tertiary" size="xsmall" text="Annuler" :disabled="saving" @click="emit('cancel')" />
          <SourceButton
            type="primary"
            size="xsmall"
            :text="saving ? 'Enregistrement…' : 'Enregistrer'"
            :disabled="saving"
            :aria-busy="saving"
            @click="emit('save')"
          />
        </div>
      </div>
    </Transition>
  </Teleport>

  <ConfirmOverlay
    :show="showDiscardConfirm"
    :title="DISCARD_UNSAVED_TITLE"
    :description="DISCARD_UNSAVED_DESCRIPTION"
    :confirm-text="DISCARD_UNSAVED_CONFIRM_TEXT"
    :cancel-text="DISCARD_UNSAVED_CANCEL_TEXT"
    variant="warning"
    @close="emit('cancelDiscard')"
    @confirm="emit('confirmDiscard')"
  />
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as colors;
@use "~/assets/scss/partials/elpdl/spacing" as spacing;
@use "~/assets/scss/partials/elpdl/elevation" as elevation;
@use "~/assets/scss/partials/elpdl/layout" as layout;

/* 3.5rem = gabarit du FAB toolbox (56px), pour coller l’overlay à sa gauche. */
$toolbox-fab: 3.5rem;

.org-reorder-live {
  @include layout.visually-hidden;
}

.org-reorder-overlay {
  position: fixed;
  right: calc(#{spacing.$spacing-l} + #{$toolbox-fab} + #{spacing.$spacing-s});
  z-index: 11900;
  display: flex;
  align-items: center;
  gap: spacing.$spacing-s;
  max-width: calc(100vw - #{spacing.$spacing-l} - #{$toolbox-fab} - #{spacing.$spacing-s} - #{spacing.$spacing-m});
  margin: 0;
  padding: spacing.$spacing-s spacing.$spacing-m;
  background: colors.$enedis-primary-200;
  border: 1px solid colors.$enedis-primary-300;
  border-radius: spacing.$spacing-3xs;
  box-sizing: border-box;
  @include elevation.elevation-100;
}

.org-reorder-overlay__icon {
  flex-shrink: 0;
  color: colors.$enedis-primary-500;
  font-size: 1.125rem;
  font-variation-settings: "FILL" 0, "wght" 500, "GRAD" 0, "opsz" 24;
}

.org-reorder-overlay__label {
  margin: 0;
  color: colors.$enedis-neutral-900;
  font-weight: 600;
}

.org-reorder-overlay__actions {
  display: flex;
  gap: spacing.$spacing-s;
}

.org-reorder-overlay-enter-active,
.org-reorder-overlay-leave-active {
  transition: opacity 0.22s ease, transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.org-reorder-overlay-enter-from,
.org-reorder-overlay-leave-to {
  opacity: 0;
  transform: translateX(spacing.$spacing-s);
}

@media (prefers-reduced-motion: reduce) {
  .org-reorder-overlay-enter-active,
  .org-reorder-overlay-leave-active {
    transition: none;
  }
}
</style>
